import { Box, Button, FormGroup, Grid, Paper, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "reactstrap";
import { isValidBelgianRijksregisternummer } from "../Helpers/BelgianRrn";
import AuthenticationService from "../Services/AuthenticationService";
import { toast } from "react-toastify";
import { useAuth } from "../AuthContext";
import { useSearchParams } from "react-router-dom";

export default function CodeLogin() {
  const auth = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();

  const { t } = useTranslation("home");
  const authenticationService = useMemo(() => new AuthenticationService(), []);

  const [showLocalLogin, setShowLocalLogin] = useState<boolean>(false);
  const [codeRequested, setCodeRequested] = useState<boolean>(false);

  const [rrn, setRrn] = useState<string>(
    sessionStorage.getItem("SurveyAppCodeLoginRrn") ?? ""
  );
  const [rrnError, setRrnError] = useState<string>("");

  const [mobilePhone, setMobilePhone] = useState<string>(
    sessionStorage.getItem("SurveyAppCodeLoginMobilePhone") ?? ""
  );
  const [mobilePhoneError, setMobilePhoneError] = useState<string>("");

  const [email, setEmail] = useState<string>(
    sessionStorage.getItem("SurveyAppCodeLoginEmail") ?? ""
  );
  const [emailError, setEmailError] = useState<string>("");

  const [code, setCode] = useState<string>("");
  const [codeError, setCodeError] = useState<string>("");

  function rrnValid(): boolean {
    if (rrn.length === 0) return false;

    return isValidBelgianRijksregisternummer(rrn);
  }

  async function sendCode(by: string) {
    setRrnError("");
    setEmailError("");
    setMobilePhoneError("");

    let error = false;
    if (!rrnValid()) {
      setRrnError(t("Ongeldig rijksregisternummer / bisnummer") ?? "");
      error = true;
    }

    if (by === "sms") {
      if (mobilePhone.length === 0) {
        setMobilePhoneError(t("Ongeldig telefoonnummer") ?? "");
        error = true;
      }
    }

    if (by === "email") {
      if (email.length === 0) {
        setEmailError(t("Ongeldig email") ?? "");
        error = true;
      }
    }

    if (!error) {
      const result = await authenticationService.sendAuthenticationCode({
        url: "" + process.env.REACT_APP_ENV_URL,
        mobilePhone: by === "sms" ? mobilePhone : "",
        emailAddress: by === "email" ? email : "",
        insz: rrn,
      });

      if (result.success) {
        setCodeRequested(true);
      } else {
        setCodeRequested(false);
        toast.error(t("Fout bij aanvragen van code. Probeer opnieuw."));
      }
    }
  }

  async function checkCode() {
    if (code.length === 0) {
      setCodeError(t("Ongeldige code") ?? "");
    } else {
      const result = await authenticationService.checkAuthenticationCode({
        code: code,
        insz: rrn,
      });

      if (result.success) {
        auth.setLocalAuthenticated(result.insz);
      } else {
        setCodeError(result.message);
      }
    }
  }

  useEffect(() => {
    const step = searchParams.get("step");
    if (step === "1") {
      setShowLocalLogin(true);
    } else if (step === "2") {
      setShowLocalLogin(true);
      setCodeRequested(true);
    }
  }, []);

  useEffect(() => {
    if (showLocalLogin) {
      setSearchParams((searchParams) => {
        searchParams.set("step", "1");
        return searchParams;
      });
    }
  }, [showLocalLogin]);

  useEffect(() => {
    if (codeRequested) {
      setSearchParams((searchParams) => {
        searchParams.set("step", "2");
        return searchParams;
      });
    } else {
      setSearchParams((searchParams) => {
        searchParams.set("step", "1");
        return searchParams;
      });
    }
  }, [codeRequested]);

  useEffect(() => {
    sessionStorage.setItem("SurveyAppCodeLoginRrn", rrn);
  }, [rrn]);

  useEffect(() => {
    sessionStorage.setItem("SurveyAppCodeLoginMobilePhone", mobilePhone);
  }, [mobilePhone]);

  useEffect(() => {
    sessionStorage.setItem("SurveyAppCodeLoginEmail", email);
  }, [email]);

  return (
    <>
      <Box>
        {!showLocalLogin && (
          <Button variant="contained" onClick={() => setShowLocalLogin(true)}>
            {t("Inloggen met je rijksregisternummer / bisnummer")}
          </Button>
        )}
        {showLocalLogin && !codeRequested && (
          <Grid container spacing={2}>
            <Grid item xs={0} md={4} />
            <Grid item xs={12} md={4}>
              <Paper elevation={3}>
                <Box p={2}>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                    }}
                  >
                    <FormGroup className="ms-2 me-2 mt-2 mb-2">
                      <TextField
                        className="mt-3 mb-3"
                        fullWidth
                        margin="dense"
                        id="rrn"
                        label={t("Rijksregisternummer / bisnummer")}
                        variant="outlined"
                        required
                        error={rrnError.length > 0}
                        helperText={rrnError}
                        name="rrn"
                        placeholder="00.00.00-000.00"
                        value={rrn}
                        onChange={(e) => setRrn(e.target.value)}
                        autoFocus={true}
                        title={
                          t(
                            "Voer 11 cijfers in zonder spaties of tekens, zoals 00.00.00-000.00"
                          ) ?? ""
                        }
                      />
                    </FormGroup>
                    <FormGroup className="ms-2 me-2 mt-2 mb-2">
                      <TextField
                        className="mt-3 mb-3"
                        fullWidth
                        margin="dense"
                        id="mobilePhone"
                        label={t("Telefoonnummer")}
                        variant="outlined"
                        name="mobilePhone"
                        value={mobilePhone}
                        required={(email ?? "") === ""}
                        error={mobilePhoneError.length > 0}
                        helperText={mobilePhoneError}
                        placeholder="+324********"
                        onChange={(e) => setMobilePhone(e.target.value)}
                      />
                    </FormGroup>
                    <Button
                      variant="contained"
                      type="button"
                      onClick={() => sendCode("sms")}
                    >
                      {t("Verstuur code via SMS")}
                    </Button>
                    <FormGroup className="ms-2 me-2 mt-2 mb-2">
                      <TextField
                        className="mt-3 mb-3"
                        fullWidth
                        margin="dense"
                        id="emailAddress"
                        label={t("Email")}
                        variant="outlined"
                        name="emailAddress"
                        value={email}
                        required={(mobilePhone ?? "") === ""}
                        error={emailError.length > 0}
                        helperText={emailError}
                        placeholder="***@***.be"
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </FormGroup>
                    <Button
                      variant="contained"
                      type="button"
                      onClick={() => sendCode("email")}
                    >
                      {t("Verstuur code via email")}
                    </Button>
                  </Form>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={0} md={4} />
          </Grid>
        )}
        {showLocalLogin && codeRequested && (
          <Grid container spacing={2}>
            <Grid item xs={0} md={4} />
            <Grid item xs={12} md={4}>
              <Paper elevation={3}>
                <Box p={2}>
                  <h2>{t("Code")}</h2>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      checkCode();
                    }}
                  >
                    <FormGroup className="ms-2 me-2 mt-2 mb-2">
                      <TextField
                        className="mt-3 mb-3"
                        fullWidth
                        margin="dense"
                        id="code"
                        label={t("Typ hier je code")}
                        variant="outlined"
                        required
                        name="code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        autoFocus={true}
                        error={codeError.length > 0}
                        helperText={codeError}
                      />
                    </FormGroup>
                    <Button
                      variant="contained"
                      className="me-2"
                      onClick={() => {
                        setCodeRequested(false);
                      }}
                    >
                      {t("Ga terug")}
                    </Button>
                    <Button
                      variant="contained"
                      type="button"
                      onClick={() => checkCode()}
                    >
                      {t("Ga verder")}
                    </Button>
                  </Form>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={0} md={4} />
          </Grid>
        )}
      </Box>
    </>
  );
}
